'use client';

import { useEffect, useId, useRef } from 'react';

/**
 * Injeta HTML vindo do Ad Inserter (WP) e recria <script> para executar.
 * target: 'inline' | 'head' | 'body-start' | 'body-end'
 */
export default function HtmlFromWp({
	html,
	target = 'inline',
	uniqueKey = '',
}) {
	const hostRef = useRef(null);
	const reactId = useId();
	const marker = `hwp-ad-${uniqueKey || reactId.replace(/:/g, '')}`;

	useEffect(() => {
		if (!html || typeof html !== 'string' || !html.trim()) {
			return undefined;
		}

		const nodes = [];
		try {
			document.querySelectorAll(`[data-hwp-ad="${marker}"]`).forEach((el) => el.remove());

			const tpl = document.createElement('template');
			tpl.innerHTML = html.trim();

			const mount = (node) => {
				if (node.nodeName === 'SCRIPT') {
					const s = document.createElement('script');
					for (const attr of node.attributes || []) {
						s.setAttribute(attr.name, attr.value);
					}
					if (node.textContent) {
						s.text = node.textContent;
					}
					s.dataset.hwpAd = marker;
					return s;
				}
				if (node.nodeType === 1) {
					node.dataset.hwpAd = marker;
				}
				return node;
			};

			const appendTo = (parent, asFirst = false) => {
				Array.from(tpl.content.childNodes).forEach((child) => {
					const n = mount(child.cloneNode(true));
					if (asFirst && parent.firstChild) {
						parent.insertBefore(n, parent.firstChild);
					} else {
						parent.appendChild(n);
					}
					nodes.push(n);
				});
			};

			if (target === 'head') {
				appendTo(document.head);
			} else if (target === 'body-start') {
				appendTo(document.body, true);
			} else if (target === 'body-end') {
				appendTo(document.body, false);
			} else if (hostRef.current) {
				hostRef.current.innerHTML = '';
				appendTo(hostRef.current, false);
			}
		} catch (err) {
			console.warn('[HtmlFromWp]', err);
		}

		return () => {
			nodes.forEach((n) => {
				try {
					n.remove();
				} catch {}
			});
			document.querySelectorAll(`[data-hwp-ad="${marker}"]`).forEach((el) => el.remove());
		};
	}, [html, target, marker]);

	if (!html || !String(html).trim()) {
		return null;
	}

	if (target !== 'inline') {
		return null;
	}

	return <div ref={hostRef} className="hwp-ad-slot" suppressHydrationWarning />;
}
